/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import {
    loginUser,
    forgotPassword,
    resetPassword,
    updateUser,
    registerUser,
    twoFactorConfirmation,
    twoFactorResend,
    logoutUser,
    getUsers,
    updateOtherUser,
    createUser,
    deleteUser,
} from './userApi';
import { UserState } from './types';

const initialState = {
    error: null,
    isFetching: false,
    isLoggedIn: false,
    showTwoFactorPage: false,
    userData: null,
    users: [],
    singleUser: null,
    isUserCreated: false,
} as UserState;

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        loadSingleUser: (state, action: PayloadAction<number>) => {
            const school = state.users.find(s => s.id === action.payload) || null;
            state.singleUser = school;
        },
        resetUserCreated: (state) => {
            state.isUserCreated = false;
        },
    },
    extraReducers: (builder) => {
        // Login user
        builder
            .addCase(loginUser.fulfilled, (state:UserState, action) => {
                state.isFetching = false;
                if (action.payload.token) {
                    state.userData = action.payload.user;
                    state.isLoggedIn = true;
                } else if (action.payload.twoFactorToken) {
                    state.showTwoFactorPage = true;
                }
            });

        // Forgot password
        builder
            .addCase(forgotPassword.fulfilled, (state:UserState) => {
                state.isFetching = false;
                notification.success({
                    message: 'Pavyko!',
                    description: 'Slaptažodžio atstatymo laiškas išsiųstas. Jei paskyra egzistuoja, netrukus gausite el. laišką.',
                });
            });

        builder.addCase(createUser.fulfilled, (state:UserState, action) => {
            const createdUser = action?.payload?.user;

            // Update singleSurvey
            state.singleUser = createdUser;
            state.isFetching = false;
            state.isUserCreated = true;
            state.users.push(createdUser);

            notification.success({
                message: 'Pavyko!',
                description: 'Vartotojas sėkmingai sukurtas, vartotojui suteikti nauji prisijungimo duomenys ir išsiųsti el. paštu',
            });
        });

        // Reset password
        builder
            .addCase(resetPassword.fulfilled, (state:UserState) => {
                state.isFetching = false;
                notification.success({
                    message: 'Success',
                    description: 'Your password has been reset, please login with your new password.',
                });
            });

        // Update user
        builder
            .addCase(updateUser.fulfilled, (state:UserState, action) => {
                state.isFetching = false;
                state.userData = action?.payload?.user;
                notification.success({
                    message: 'Success',
                    description: 'Duomenys sėkmingai atnaujinti.',
                });
            });

        builder
            .addCase(updateOtherUser.fulfilled, (state: UserState, action) => {
                state.isFetching = false;
                const updatedUser = action?.payload?.user;

                // Update singleSchool
                state.singleUser = updatedUser;

                // Update schools array
                const index = state.users.findIndex(s => s.id === updatedUser.id);
                if (index !== -1) {
                    state.users[index] = updatedUser;
                }

                notification.success({
                    message: 'Success',
                    description: 'Vartotojas sėkmingai atnaujintas',
                });
            });

        // Register user
        builder
            .addCase(registerUser.fulfilled, (state:UserState) => {
                state.isFetching = false;
                notification.success({
                    message: 'Success',
                    description: 'Your account has been created, please login.',
                });
            });

        // Two factor confirmation
        builder
            .addCase(twoFactorConfirmation.fulfilled, (state:UserState) => {
                state.isFetching = false;
                state.isLoggedIn = true;
                state.showTwoFactorPage = false;
                notification.success({
                    message: 'Success',
                    description: 'You have successfully logged in.',
                });
            });

        // Two factor resend
        builder
            .addCase(twoFactorResend.fulfilled, (state:UserState) => {
                state.isFetching = false;
                notification.success({
                    message: 'Success',
                    description: 'A new two factor code has been sent to your email.',
                });
            });
        builder
            .addCase(getUsers.fulfilled, (state:UserState, action) => {
                state.isFetching = false;
                state.users = action?.payload?.users;
            });

        builder
            .addCase(deleteUser.fulfilled, (state:UserState, action) => {
                state.isFetching = false;
                state.users = state.users.filter(s => s.id !== action?.payload?.id);
                notification.success({
                    message: 'Pavyko!',
                    description: 'Vartotojas sėkmingai ištrintas',
                });
            });

        builder.addMatcher(
            isAnyOf(
                logoutUser.fulfilled,
                logoutUser.rejected,
            ),
            (state:UserState) => {
                state.isFetching = false;
                state.isLoggedIn = false;
                state.userData = null;
                state.users = [];
                state.singleUser = null;
                notification.success({
                    message: 'Pavyko!',
                    description: 'Jūs sėkmingai atsijungėte.',
                });
                window.location.href = '/';
            },
        );

        // Pending state for all requests
        builder.addMatcher(
            isAnyOf(
                loginUser.pending,
                forgotPassword.pending,
                resetPassword.pending,
                registerUser.pending,
                twoFactorConfirmation.pending,
                twoFactorResend.pending,
                logoutUser.pending,
            ),
            (state:UserState) => {
                state.isFetching = true;
                state.isLoggedIn = false;
                state.error = null;
                state.userData = null;
            },
        );
        builder.addMatcher(
            isAnyOf(
                twoFactorConfirmation.pending,
                twoFactorResend.pending,
                updateUser.pending,
                getUsers.pending,
                updateOtherUser.pending,
                createUser.pending,
                deleteUser.pending,
            ),
            (state:UserState) => {
                state.isFetching = true;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                loginUser.rejected,
                forgotPassword.rejected,
                resetPassword.rejected,
                registerUser.rejected,
                twoFactorConfirmation.rejected,
                twoFactorResend.rejected,
                updateUser.rejected,
                getUsers.rejected,
                updateOtherUser.rejected,
                createUser.rejected,
                deleteUser.rejected,
            ),
            (state:UserState, action) => {
                state.isFetching = false;
                state.error = action.error;
            },
        );
    },
});

export const { loadSingleUser, resetUserCreated } = userSlice.actions;
export default userSlice.reducer;
